import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, Loader2, Send, Trash2, X, HandHeart, CornerDownRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ChatAvatar from "@/components/ChatAvatar";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const MILESTONES = [10, 25, 50, 100];

// Modal thread of short notes/questions on an event, with replies + Amen reactions.
export default function EventComments({ ev, onClose, onCountChange }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [burstId, setBurstId] = useState(null);
  const endRef = useRef(null);

  const load = () => api.get(`/events/${ev.id}/comments`).then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [items.length]);

  // Group into top-level comments + their replies.
  const threads = useMemo(() => {
    const roots = items.filter((c) => !c.parent_id);
    const byParent = {};
    items.filter((c) => c.parent_id).forEach((r) => { (byParent[r.parent_id] = byParent[r.parent_id] || []).push(r); });
    return roots.map((r) => {
      const replies = byParent[r.id] || [];
      let topReplyId = null;
      const best = [...replies].sort((a, b) => b.amen_count - a.amen_count)[0];
      if (best && best.amen_count > 0) topReplyId = best.id;
      const sorted = topReplyId ? [best, ...replies.filter((x) => x.id !== topReplyId)] : replies;
      return { ...r, replies: sorted, topReplyId };
    });
  }, [items]);

  const post = async (body, after) => {
    if (!user) { toast.error("Sign in to comment"); return; }
    try {
      const { data } = await api.post(`/events/${ev.id}/comments`, body);
      setItems((p) => [...p, data]);
      onCountChange?.(items.length + 1);
      after?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post"); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    await post({ text }, () => setText(""));
    setSending(false);
  };

  const sendReply = async (parent) => {
    if (!replyText.trim()) return;
    await post({ text: replyText, parent_id: parent.id }, () => { setReplyText(""); setReplyTo(null); });
  };

  const del = async (c) => {
    try { await api.delete(`/events/${ev.id}/comments/${c.id}`); setItems((p) => p.filter((x) => x.id !== c.id && x.parent_id !== c.id)); onCountChange?.(items.length - 1); }
    catch (e) { toast.error("Could not delete"); }
  };

  const amen = async (c) => {
    if (!user) { toast.error("Sign in to react"); return; }
    try {
      const { data } = await api.post(`/events/${ev.id}/comments/${c.id}/amen`);
      setItems((p) => p.map((x) => (x.id === c.id ? { ...x, i_amened: data.i_amened, amen_count: data.amen_count } : x)));
      if (data.i_amened && MILESTONES.includes(data.amen_count)) {
        setBurstId(c.id);
        setTimeout(() => setBurstId((b) => (b === c.id ? null : b)), 1200);
        toast.success(`🎉 ${data.amen_count} Amens! This one's spreading joy.`);
      }
    } catch (e) { toast.error("Could not react"); }
  };

  const canManage = (c) => user?.role === "admin" || user?.id === c.user_id;

  const CommentRow = ({ c, isReply, isTop }) => (
    <div className={`flex gap-3 ${isReply ? "ml-8 mt-3" : ""}`} data-testid={`comment-${c.id}`}>
      {isReply && <CornerDownRight className="w-4 h-4 text-zinc-600 mt-2 shrink-0" />}
      <ChatAvatar userId={c.user_id} name={c.user_name} size={isReply ? 26 : 32} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{c.user_name || "Member"}</span>
          {isTop && <span data-testid={`top-reply-${c.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#E6B34A]/20 text-[#E6B34A]"><HandHeart className="w-3 h-3" /> Top reply</span>}
          <span className="text-[11px] text-zinc-600">{timeAgo(c.created_at)}</span>
          {canManage(c) && <button onClick={() => del(c)} data-testid={`comment-del-${c.id}`} className="ml-auto text-zinc-600 hover:text-[#FF3B30]"><Trash2 className="w-3.5 h-3.5" /></button>}
        </div>
        <p className="text-sm text-zinc-300 mt-0.5 break-words">{c.text}</p>
        <div className="flex items-center gap-3 mt-1.5">
          <button onClick={() => amen(c)} data-testid={`comment-amen-${c.id}`} className={`relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${c.i_amened ? "bg-[#E6B34A]/20 text-[#E6B34A]" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`}>
            <HandHeart className="w-3.5 h-3.5" /> Amen{c.amen_count > 0 ? ` · ${c.amen_count}` : ""}
            {burstId === c.id && (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center" data-testid={`amen-burst-${c.id}`}>
                {["-30px,-20px", "0,-32px", "30px,-20px", "-20px,-4px", "20px,-4px"].map((t, i) => (
                  <span key={i} className="amen-burst-emoji" style={{ ["--tx"]: t.split(",")[0], ["--ty"]: t.split(",")[1], animationDelay: `${i * 40}ms` }}>🙏</span>
                ))}
              </span>
            )}
          </button>
          {!isReply && user && <button onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyText(""); }} data-testid={`comment-reply-btn-${c.id}`} className="text-xs text-zinc-500 hover:text-[#E6B34A] font-semibold">Reply</button>}
        </div>
        {replyTo === c.id && (
          <form onSubmit={(e) => { e.preventDefault(); sendReply(c); }} className="flex items-center gap-2 mt-2">
            <input autoFocus value={replyText} onChange={(e) => setReplyText(e.target.value)} data-testid={`comment-reply-input-${c.id}`} placeholder="Write a reply…" className="flex-1 px-3 py-2 rounded-lg bg-[#0C1338] border border-white/10 text-xs text-white focus:border-[#E6B34A] focus:outline-none" />
            <button type="submit" data-testid={`comment-reply-send-${c.id}`} disabled={!replyText.trim()} className="p-2 rounded-lg bg-[#E6B34A] text-[#0A1030] disabled:opacity-50"><Send className="w-4 h-4" /></button>
          </form>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="event-comments-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg h-[80vh] flex flex-col rounded-3xl border border-white/10 bg-[#141C42] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#E6B34A]" />
            <h3 className="font-display font-bold text-lg truncate">{ev.title}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5" data-testid="event-comments-list">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#E6B34A]" /></div> :
            threads.length === 0 ? <p className="text-center text-zinc-500 text-sm py-10">Be the first to say something 🙌</p> :
            threads.map((c) => (
              <div key={c.id}>
                <CommentRow c={c} />
                {c.replies.map((r) => <CommentRow key={r.id} c={r} isReply isTop={r.id === c.topReplyId} />)}
              </div>
            ))}
          <div ref={endRef} />
        </div>

        {user ? (
          <form onSubmit={send} className="p-4 border-t border-white/5 flex items-center gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} data-testid="comment-input" placeholder="Add a note or question…" className="flex-1 px-4 py-2.5 rounded-xl bg-[#0C1338] border border-white/10 text-sm text-white focus:border-[#E6B34A] focus:outline-none" />
            <button type="submit" disabled={sending || !text.trim()} data-testid="comment-send" className="p-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] disabled:opacity-50">
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </form>
        ) : <p className="p-4 border-t border-white/5 text-center text-sm text-zinc-500">Sign in to join the conversation.</p>}
      </div>
    </div>
  );
}
